"use client";

import React from "react";
import { motion, useReducedMotion } from "framer-motion";
import { CircleCheck as CheckCircle2, MapPin, Quote, ShieldCheck, Sparkles, Star } from 'lucide-react';
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
const testimonials = [{
  id: "review-1",
  author: "Marcus Vance",
  initials: "MV",
  role: "Frequent Flier",
  route: "Naperville → O'Hare",
  service: "Executive Airport Transfer",
  quote: "My 4:30 AM airport runs used to feel like gambling with cancellations and surging prices. With Santa Limo, my chauffeur is outside five minutes early every time, and the price is locked the minute I book.",
  badge: "Verified passenger",
  rating: 5
}, {
  id: "review-2",
  author: "Elena Rostova",
  initials: "ER",
  role: "Managing Director",
  route: "Chicago Loop → Midway",
  service: "Corporate Chauffeur",
  quote: "Having a proudly branded, immaculate sedan waiting curbside makes a significant difference after long client meetings. The flat rate never fluctuates, even during peak Friday evening traffic.",
  badge: "Corporate account",
  rating: 5
}, {
  id: "review-3",
  author: "David Callahan",
  initials: "DC",
  role: "Private Client",
  route: "Rockford → Downtown Chicago",
  service: "Regional Long-Distance",
  quote: "Long-distance regional trips are usually where other car services tack on hidden fees. Here, everything was clear upfront, the card transaction was seamless, and the car was whisper-quiet.",
  badge: "Regional traveler",
  rating: 5
}];
const highlights = ["Flat-rate pricing locked on reservation", "Complimentary 15-minute airport wait time", "Pristine executive sedans and SUVs"];
export default function Feedback() {
  const shouldReduceMotion = useReducedMotion();
  const containerVariants = {
    hidden: {},
    show: {
      transition: {
        staggerChildren: shouldReduceMotion ? 0 : 0.12
      }
    }
  };
  const cardVariants = {
    hidden: {
      opacity: 0,
      y: shouldReduceMotion ? 0 : 20
    },
    show: {
      opacity: 1,
      y: 0,
      transition: {
        duration: shouldReduceMotion ? 0.01 : 0.45,
        ease: [0.16, 1, 0.3, 1]
      }
    }
  };
  return <section id="customer-feedback" data-nav="dark" className="dark bg-background text-foreground relative overflow-hidden py-20 lg:py-28">
      {/* Background ambient lighting */}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 flex items-center justify-center overflow-hidden">
        <div className="h-[28rem] w-[54rem] bg-primary/5 blur-3xl" />
        <div className="absolute top-0 inset-x-0 h-px bg-gradient-to-r from-transparent via-border to-transparent" />
      </div>

      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        {/* Section Header */}
        <div className="mx-auto max-w-3xl text-center">
          <div className="mb-4 inline-flex items-center gap-2 rounded-lg border border-primary/30 bg-primary/10 px-3.5 py-1 text-xs font-semibold tracking-wider text-primary uppercase">
            <ShieldCheck className="size-3.5 text-primary" />
            <span>Client Testimonials</span>
          </div>

          <h2 className="text-3xl font-bold tracking-tight text-foreground sm:text-4xl lg:text-5xl font-default">
            Quiet rides, <span className="italic text-primary">guaranteed</span> rates.
          </h2>

          <p className="mt-4 text-base text-muted-foreground sm:text-lg">
            Experience verified passenger stories from business executives, airport commuters, and regional travelers across Illinois.
          </p>

          {/* Quick trust metrics */}
          <div className="mt-6 flex flex-wrap items-center justify-center gap-x-6 gap-y-2 text-xs text-muted-foreground sm:text-sm">
            {highlights.map((item, index) => <span key={index} className="inline-flex items-center gap-1.5">
                <CheckCircle2 className="size-3.5 text-primary" />
                <span>{item}</span>
              </span>)}
          </div>
        </div>

        {/* Testimonials Grid with Redesigned Cards */}
        <motion.div variants={containerVariants} initial="hidden" whileInView="show" viewport={{
        once: true,
        amount: 0.15
      }} className="mt-14 grid grid-cols-1 gap-6 lg:grid-cols-3">
          {testimonials.map((item, i) => <motion.div key={item.id} data-index={i} variants={cardVariants} className="group relative flex flex-col justify-between rounded-xl border border-border bg-card p-6 sm:p-7 transition-[border-color,box-shadow] duration-300 hover:border-primary/40 hover:shadow-xl">
              {/* Lounge hairline accent on top border */}
              <div aria-hidden="true" className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-primary/40 to-transparent" />

              <div>
                {/* Top Bar: Route pill & Star Rating */}
                <div className="flex flex-wrap items-center justify-between gap-2 border-b border-border/70 pb-4">
                  <div className="inline-flex items-center gap-1.5 rounded-lg border border-primary/25 bg-accent/40 px-2.5 py-1 text-xs font-mono text-accent-foreground">
                    <MapPin className="size-3 text-primary" />
                    <span>{item.route}</span>
                  </div>

                  <div className="flex items-center gap-0.5" aria-label={`Rating: ${item.rating} out of 5 stars`}>
                    {Array.from({
                  length: item.rating
                }).map((_, starIndex) => <Star key={starIndex} className="size-3.5 fill-primary text-primary" aria-hidden="true" />)}
                  </div>
                </div>

                {/* Service Tag & Quote Icon */}
                <div className="mt-4 flex items-center justify-between">
                  <span className="text-xs font-medium tracking-wide text-primary uppercase">
                    {item.service}
                  </span>
                  <Quote className="size-5 text-muted-foreground/30 transition-colors group-hover:text-primary/70" />
                </div>

                {/* Testimonial Quote Text */}
                <p className="mt-3.5 text-sm leading-relaxed text-foreground sm:text-base">
                  &ldquo;{item.quote}&rdquo;
                </p>
              </div>

              {/* Author Footer */}
              <div className="mt-6 border-t border-border/70 pt-5">
                <div className="flex items-center justify-between gap-3">
                  <div className="flex items-center gap-3">
                    <div className="flex size-9 items-center justify-center rounded-lg border border-primary/30 bg-secondary text-xs font-bold text-primary font-default">
                      {item.initials}
                    </div>
                    <div>
                      <h3 className="text-sm font-semibold text-foreground font-default leading-tight">
                        {item.author}
                      </h3>
                      <p className="text-xs text-muted-foreground">{item.role}</p>
                    </div>
                  </div>

                  <div className="inline-flex items-center gap-1 rounded-lg border border-border bg-secondary px-2.5 py-1 text-xs font-medium text-secondary-foreground">
                    <Sparkles className="size-3 text-primary" />
                    <span>{item.badge}</span>
                  </div>
                </div>
              </div>
            </motion.div>)}
        </motion.div>

        {/* Section Bottom CTA Strip */}
        <div className="mt-14 flex flex-col items-center justify-center gap-4 rounded-xl border border-border bg-card/60 p-6 text-center sm:flex-row sm:justify-between sm:text-left">
          <div className="space-y-1">
            <h4 className="text-base font-semibold text-foreground font-default">
              Ready for executive ground travel?
            </h4>
            <p className="text-sm text-muted-foreground">
              Guaranteed flat quotes with no surge pricing, no meters, and transparent receipting.
            </p>
          </div>

          <Button asChild variant="default" size="default" className="shrink-0 shadow-md hover:shadow-lg">
            <Link to="/OrderPage">Book your flat-rate ride</Link>
          </Button>
        </div>
      </div>
    </section>;
}